"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useABTest } from "./useABTest";
import { useUtms } from "./useUtms";
import { FEATURE_FLAGS, HOME_VARIANTS, HomeVariant } from "@/lib/ab-test";
import { CTAS, HEADLINES, SOCIAL_PROOF, TAGLINES } from "@/lib/masterclass";
import { trackEvent, registerSuperProps } from "@/lib/analytics";
import { sendMetaPageView } from "@/lib/meta-capi";
import { sendBooking, sendLead } from "@/lib/lead";
import { readCalBooking } from "@/lib/cal";
import { getPrefill } from "@/lib/lead-prefill";
import { TRACKING_EVENTS } from "@/lib/posthog";
import { FormData, isQualified, firstNameOf } from "@/lib/form";
import { appendUtms, type Utms } from "@/lib/utm";
import { preloadCal } from "@/lib/cal";

import {
  saveLead,
  loadLead,
  saveFunnelKind,
  loadFunnelKind,
  DEFAULT_FUNNEL_KIND,
  type FunnelLead,
  type FunnelKind,
} from "@/lib/funnel-store";

// ============================================================================
// Hook d'orchestration du funnel — SPA pilotée par la ROUTE.
//   L'étape courante vient de la route (via la prop `step`). Le lead circule
//   entre les étapes via sessionStorage (@/lib/funnel-store). Centralise
//   tracking (PostHog/Meta/GAds via trackEvent), webhook (full_submission),
//   qualification, préchargement cal.com et la navigation inter-étapes.
//
//   DEUX funnels cohabitent, distingués par la clé `funnel` envoyée au webhook
//   n8n et jointe à tous les events PostHog (super-property) :
//     • vsl         → /lp → /vsl → cal.com → /ty | /nok
//     • masterclass → /mc → /mc/ty | /mc/hc   (webinaire du jeudi 12h30)
//     • meet        → /meet → cal.com → /meet/ty   (point expert, retargeting)
//                     popup de sortie → formulaire « on vous rappelle »
//   Le QCM lui-même diffère par funnel (cf. QCM_STEPS dans @/lib/form).
//
//   Étapes / routes :
//     lp        → /lp        landing VSL + formulaire
//     vsl       → /vsl       VSL gated (redirige vers /lp sans contexte)
//     vsl-free  → /vsl-free  VSL ungated ; formulaire AVANT la prise de RDV
//     nok       → /nok       profil non qualifié (cohorte complète)
//     ty        → /ty        confirmation post-réservation
//     mc        → /mc        landing masterclass + formulaire d'inscription
//     mc-ty     → /mc/ty     place confirmée (gated → /mc sans contexte)
//     mc-hc     → /mc/hc     hors cible (gated → /mc sans contexte)
// ============================================================================

export type FunnelStep =
  | "lp"
  | "vsl"
  | "vsl-free"
  | "nok"
  | "ty"
  | "mc"
  | "mc-ty"
  | "mc-hc"
  | "meet"
  | "meet-ty";

// Funnel d'appartenance de chaque étape. `null` = étape PARTAGÉE entre les deux
// funnels (/nok, /ty) : la route ne dit rien, on relit alors la valeur stockée
// à l'entrée sur la LP.
const STEP_FUNNEL: Record<FunnelStep, FunnelKind | null> = {
  lp: "vsl",
  vsl: "vsl",
  "vsl-free": "vsl",
  nok: null,
  ty: null,
  mc: "masterclass",
  "mc-ty": "masterclass",
  "mc-hc": "masterclass",
  meet: "meet",
  "meet-ty": "meet",
};

// Étapes accessibles seulement avec un lead en session, et LP de repli associée.
// /mc/ty n'y est PAS : c'est une page de confirmation partageable, qu'on envoie
// aux leads inscrits par un autre canal (WhatsApp, formulaire de pub, à la
// main). Sans contexte de session elle s'affiche simplement sans prénom.
// /mc/hc reste gated : personne n'a de raison de recevoir une page de sortie.
const GATED_STEPS: Partial<Record<FunnelStep, string>> = {
  vsl: "/lp",
  "mc-hc": "/mc",
};

/**
 * Copie de la LP masterclass, résolue depuis les 3 tests A/B.
 * `ready` = les 3 flags sont tranchés (ou le timeout de secours a sauté) :
 * tant qu'il est faux, la LP masque la zone variante-dépendante — sans quoi on
 * affiche la variante control avant la bonne (flash A/B).
 */
export type McCopy = {
  headline: string;
  tagline: string;
  cta: string;
  /** Ligne de preuve sociale sous le sous-titre ; null = rien. */
  proof: string | null;
  ready: boolean;
};

/** Coordonnées connues avant la réservation (lien pré-rempli ou popup). */
export type MeetPrefill = { name?: string; email?: string };

export type Funnel = {
  funnel: FunnelKind;
  firstName?: string;
  variant: string;
  home: HomeVariant;
  mc: McCopy;
  utms: Utms;
  lead: FunnelLead | null;
  formOpen: boolean;
  calOpen: boolean;
  openForm: (location: string) => void;
  closeForm: () => void;
  completeForm: (data: FormData) => void;
  openBooking: () => void;
  closeCal: () => void;
  onBooked: () => void;
  /** /meet : pré-remplissage de cal.com (lien personnalisé ou popup remplie). */
  meetPrefill: MeetPrefill;
  /** /meet : résultat de la popup de sortie, null tant qu'elle n'est pas remplie. */
  meetCallback: "qualified" | "disqualified" | null;
  dismissMeetCallback: () => void;
  onMeetBooked: (data: unknown) => void;
  /** Clic sur un CTA qui n'ouvre pas de formulaire (ex. défilement vers cal.com). */
  trackCta: (location: string) => void;
  /** Clic vers le groupe WhatsApp (/mc/ty, /mc/hc). */
  trackWhatsapp: (location: string) => void;
};

export function useFunnel(step: FunnelStep, fallbackFirstName?: string): Funnel {
  const router = useRouter();
  const { variant } = useABTest(FEATURE_FLAGS.HOME_COPY);
  const home = HOME_VARIANTS[variant] ?? HOME_VARIANTS.chat_agent;

  // Les 3 tests de la LP masterclass. PostHog joint lui-même `$feature/<clé>`
  // à chaque event : inutile de repasser les variantes dans les props.
  const abHeadline = useABTest(FEATURE_FLAGS.MC_HEADLINE);
  const abTagline = useABTest(FEATURE_FLAGS.MC_TAGLINE);
  const abCta = useABTest(FEATURE_FLAGS.MC_CTA);
  const abProof = useABTest(FEATURE_FLAGS.MC_PROOF);
  const mc: McCopy = {
    headline: HEADLINES[abHeadline.variant] ?? HEADLINES.equipe,
    tagline: TAGLINES[abTagline.variant] ?? TAGLINES.control,
    cta: CTAS[abCta.variant] ?? CTAS.reserver,
    proof: SOCIAL_PROOF[abProof.variant] ?? null,
    ready: abHeadline.ready && abTagline.ready && abCta.ready && abProof.ready,
  };

  const utms = useUtms();

  const [lead, setLead] = useState<FunnelLead | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [meetPrefill, setMeetPrefill] = useState<MeetPrefill>({});
  const [meetCallback, setMeetCallback] = useState<"qualified" | "disqualified" | null>(null);

  // Funnel d'origine. Valeur initiale DÉTERMINISTE (dérivée de la seule route)
  // pour ne pas casser l'hydratation : les étapes partagées sont affinées
  // depuis sessionStorage dans l'effet ci-dessous.
  const [funnel, setFunnel] = useState<FunnelKind>(
    STEP_FUNNEL[step] ?? DEFAULT_FUNNEL_KIND
  );

  // Hydrate le lead + applique le gate d'entrée + tracking d'étape au montage.
  useEffect(() => {
    const stored = loadLead();
    setLead(stored);

    // Étapes gated : sans contexte (pas passé par le form), retour à la LP.
    const fallback = GATED_STEPS[step];
    if (fallback && !stored) {
      router.replace(appendUtms(fallback));
      return;
    }

    // Résolution du funnel : la route fait foi si elle le dit, sinon on relit
    // ce qui a été mémorisé à l'entrée (cas des étapes partagées /nok, /ty).
    const routeFunnel = STEP_FUNNEL[step];
    const resolved = routeFunnel ?? loadFunnelKind();
    if (routeFunnel) saveFunnelKind(routeFunnel);
    setFunnel(resolved);

    // Super-property : `funnel` est joint à TOUS les events PostHog, y compris
    // ceux émis par le formulaire (useQualificationForm). À enregistrer AVANT
    // le premier trackEvent de la page.
    registerSuperProps({ funnel: resolved });

    trackEvent(TRACKING_EVENTS.PAGE_VIEW, { funnel: resolved, variant, etape: step });
    // PageView serveur (Meta CAPI), dédupliqué avec le Pixel, sur les landings.
    if (step === "lp" || step === "mc" || step === "meet") sendMetaPageView();
    // /meet : un lien personnalisé (?nom=&email=) pré-remplit la réservation.
    if (step === "meet") {
      const p = getPrefill();
      setMeetPrefill({ name: p.nomComplet || stored?.nomComplet, email: p.email || stored?.email });
    }
    // Précharge cal.com dès l'arrivée sur une page vidéo pour un profil qualifié.
    if ((step === "vsl" || step === "vsl-free") && stored?.qualified) preloadCal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Tout event du funnel porte le couple (funnel, variante A/B).
  const track = (event: string, props?: Record<string, unknown>) =>
    trackEvent(event, { funnel, variant, ...props });

  const leadProps = (f: FormData, qualified: boolean) => ({
    statut: f.statut,
    maturite_ia: f.maturiteIA,
    taille_entreprise: f.tailleEntreprise,
    outil_principal: f.outilPrincipal,
    taille_equipe: f.tailleEquipe,
    qualified,
  });

  const openForm = (location: string) => {
    track(TRACKING_EVENTS.CTA_CLICK, { location });
    setFormOpen(true);
  };
  const closeForm = () => setFormOpen(false);

  // Fin du QCM : qualification + webhook complet, PUIS routage selon l'étape.
  const completeForm = (data: FormData) => {
    const qualified = isQualified(data);
    const next: FunnelLead = {
      nomComplet: data.nomComplet,
      email: data.email,
      phone: data.phone,
      firstName: firstNameOf(data.nomComplet),
      qualified,
      variant,
    };
    saveLead(next);
    setLead(next);

    sendLead("full_submission", data);
    track(TRACKING_EVENTS.FORM_SUBMISSION, leadProps(data, qualified));
    track(
      qualified ? TRACKING_EVENTS.LEAD_QUALIFIED : TRACKING_EVENTS.LEAD_DISQUALIFIED,
      leadProps(data, qualified)
    );
    setFormOpen(false);

    // --- Funnel masterclass : l'inscription au direct EST la conversion. ---
    if (step === "mc") {
      if (qualified) {
        track(TRACKING_EVENTS.SEAT_CONFIRMED);
        router.push(appendUtms("/mc/ty"));
      } else {
        router.push(appendUtms("/mc/hc"));
      }
      return;
    }

    // --- /meet : popup de sortie remplie → on rappelle ------------------------
    // Le lead est déjà au webhook (full_submission) ; qualifié, on garde en
    // plus le calendrier pré-rempli sous la main.
    if (step === "meet") {
      if (qualified) setMeetPrefill({ name: data.nomComplet, email: data.email });
      setMeetCallback(qualified ? "qualified" : "disqualified");
      return;
    }

    // --- Funnel VSL --------------------------------------------------------
    track(TRACKING_EVENTS.VSL_UNLOCKED, { qualified });

    // Depuis la landing → on envoie voir la VSL (gated).
    if (step === "lp") {
      router.push(appendUtms("/vsl"));
      return;
    }
    // Depuis /vsl-free (form demandé AVANT la résa) → RDV direct ou /nok.
    if (qualified) {
      track(TRACKING_EVENTS.CAL_MODAL_OPENED);
      preloadCal();
      setCalOpen(true);
    } else {
      track(TRACKING_EVENTS.BOOKING_UNAVAILABLE);
      router.push(appendUtms("/nok"));
    }
  };

  // CTA "Réserver" sur /vsl et /vsl-free.
  const openBooking = () => {
    track(TRACKING_EVENTS.VSL_CTA_CLICK, { qualified: lead?.qualified ?? null });
    // /vsl-free sans contexte : on qualifie via le formulaire AVANT la résa.
    if (!lead) {
      openForm("vsl_booking");
      return;
    }
    if (lead.qualified) {
      track(TRACKING_EVENTS.CAL_MODAL_OPENED);
      setCalOpen(true);
    } else {
      track(TRACKING_EVENTS.BOOKING_UNAVAILABLE);
      router.push(appendUtms("/nok"));
    }
  };
  const closeCal = () => setCalOpen(false);

  // Réservation confirmée (event cal.com `bookingSuccessful`) → /ty.
  const onBooked = () => {
    const l = lead ?? loadLead();
    if (l) {
      track(TRACKING_EVENTS.APPLICATION_SUBMITTED, {
        email: l.email,
        qualified: true,
      });
    }
    // petit délai pour laisser le beacon analytics partir avant la navigation
    if (typeof window !== "undefined") {
      window.setTimeout(() => {
        window.location.href = appendUtms("/ty");
      }, 400);
    }
  };

  // /meet : RDV pris sur cal.com — LA conversion du funnel. Coordonnées lues
  // dans la réservation, envoyées au webhook (booking_confirmed) et mémorisées
  // pour que les régies reçoivent l'email haché (Meta CAPI, OpenAI).
  const onMeetBooked = (data: unknown) => {
    const booking = readCalBooking(data, { name: meetPrefill.name, email: meetPrefill.email });
    const known = loadLead();
    if (booking.email) {
      saveLead({
        nomComplet: booking.name ?? known?.nomComplet ?? "",
        email: booking.email,
        phone: booking.phone ?? known?.phone ?? "",
        firstName: firstNameOf(booking.name ?? known?.nomComplet ?? ""),
        qualified: true,
        variant,
      });
    }
    sendBooking(booking);
    track(TRACKING_EVENTS.APPLICATION_SUBMITTED, {
      email: booking.email,
      booking_start: booking.startTime,
    });
    // Le prénom de /meet/ty vient de la session : rien de personnel dans l'URL.
    window.setTimeout(() => {
      window.location.href = appendUtms("/meet/ty");
    }, 400);
  };

  return {
    funnel,
    // Prénom : la session prime, sinon celui passé dans l'URL (lead inscrit
    // par un autre canal), sinon rien — la page reste lisible sans.
    firstName: lead?.firstName || fallbackFirstName || undefined,
    variant,
    home,
    mc,
    utms,
    lead,
    formOpen,
    calOpen,
    openForm,
    closeForm,
    completeForm,
    openBooking,
    closeCal,
    onBooked,
    meetPrefill,
    meetCallback,
    dismissMeetCallback: () => setMeetCallback(null),
    onMeetBooked,
    trackCta: (location: string) => track(TRACKING_EVENTS.CTA_CLICK, { location }),
    trackWhatsapp: (location: string) => track(TRACKING_EVENTS.WHATSAPP_JOIN, { location }),
  };
}
